import React, { PureComponent, Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router-dom';
import Enrollment from './enrollment';
import CreateAccount from './create-account';
import ContactInformation from './contact-information';
import PracticeInformation from './practice-information';
import Ownership from './ownership';
import AddOwners from './add-owners';
import SignificantOwner from './significant-owner';
import PaymentPlans from './payment-plans';
import AchInformation from './ach-information';
import Review from './review';
import Agreement from './agreement';
import ThankYou from './thank-you';
import { actions } from '../store';
import { NavigationHeader, Header } from './header';
import ProgressBar from './progress-bar';
import { applicationInReview } from '../store/actions/onboarding';
import { APPLICATION_STATUSES } from '../api/constants';
import { WarningNotificationBanner } from './notification-banner';

const getHeader = (props) => {
  const { location } = props;
  const showLogoutLinkValidation = /(contact-information|practice-information|ownership|add-owners|significant-owner|payment-plans|ach-information|review|agreement|thank-you)/;

  const showHelp = showLogoutLinkValidation.exec(location.pathname);

  const navlinks = showHelp
    ? [{ href: '/patientsolutions/enroll/logout', className: 'sign-out-link', title: 'Sign Out' }]
    : props.navlinks;

  const navigationHeaderProps = showHelp
    ? { helpfulMsg: <div className='header-help header-help--with-nav'>Questions? Give us a call at (800) 630-1663</div>}
    : {};

  return /create-account/.exec(location.pathname) ? <Header {...props}>
    <div className='header-help'>Questions? Give us a call at (800) 630-1663</div>
  </Header> : <NavigationHeader {...navigationHeaderProps} {...props} navlinks={navlinks} />;
};

const ApplicationRestrictedBanner = ({ onboarding, location}) => {
  const {  hasCompletedReview, enrollmentComplete } = onboarding;
  const isSignAgreementPage = /agreement/.test(location.pathname);
  const isThankYouPage = /thank-you/.test(location.pathname);

  const displayWarningBanner = isSignAgreementPage
    ? enrollmentComplete
    : !isThankYouPage && hasCompletedReview;

  const message = enrollmentComplete
    ? 'Application will no longer accept changes.'
    : 'Please return to the Sign Agreement Page, application will no longer accept changes.'
  
  return displayWarningBanner && <WarningNotificationBanner
    message={message}>
    <span>Contact us at <a href='tel:18006301663'><b>(800) 630-1663</b></a> for more details.</span>
  </WarningNotificationBanner>
};

// https://reacttraining.com/react-router/web/guides/scroll-restoration
class ScrollToTop extends Component {
  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      window.scrollTo(0, 0);
    }
  }

  render() {
    return this.props.children;
  }
}

class Main extends PureComponent {
  componentDidMount = () => {
    window && (window.onpopstate = (e) => {
      const { history, onboarding } = this.props;
      if (/create-account/.exec(document.location.pathname) && onboarding.status === APPLICATION_STATUSES.REGISTERED) {
        history.push('/patientsolutions/enroll/contact-information');
      }
      window.scroll(0, 0);
    });
  }

  render() {
    const { onboarding, smartystreetsconfig, location, displayLegalUpdates } = this.props;
    const { percentage } = onboarding;
    const isInReview = applicationInReview(location) || this.props.isInReview;
    const copyrightText = smartystreetsconfig ? `2006-${smartystreetsconfig.copyrightEndYear} ${smartystreetsconfig.organization}. All rights reserved.` : '';
    const additionalFooter = location.pathname==='/patientsolutions/enroll';
    const getAdditionalFooterContent = () => (
      <>
        <p>By providing your email address, you agree that we may send promotional information about our products and services to you via email. You may opt out any time.</p>
        <p>By providing your telephone number, you agree that we may contact you via telephone with promotional information about our products and services. This includes contacting you using prerecorded or artificial voice message calls, text messages, and calls made by an automatic telephone dialing system. Standard data and message rates will apply. You understand your consent to receive promotional information via telephone is not required to enter into any agreement with LendingClub Bank. You may opt out at any time.</p>
      </>
      );

    return <ScrollToTop {...this.props}>
        { getHeader(this.props) }
        <ApplicationRestrictedBanner onboarding={onboarding} location={location} />
        <main role='main'>
          <ProgressBar percentage={percentage} />
          <Switch>
            <Route exact path='/patientsolutions/enroll/get-info' render={() => <Enrollment {...this.props} highlightMoreInfo /> } />
            <Route exact path='/patientsolutions/enroll' render={() => <Enrollment {...this.props} /> } />
            <Route exact path='/patientsolutions/enroll/create-account' render={() => <CreateAccount {...this.props} />} />
            <Route exact path='/patientsolutions/enroll/contact-information' render={() => <ContactInformation isInReview={isInReview} {...this.props} />} />
            <Route exact path='/patientsolutions/enroll/practice-information' render={() => <PracticeInformation isInReview={isInReview} {...this.props} />} />
            <Route exact path='/patientsolutions/enroll/ownership' render={() => <Ownership isInReview={isInReview} { ...this.props } /> } />
            <Route exact path='/patientsolutions/enroll/ownership/add-owners' render={() => <AddOwners isInReview={isInReview} { ...this.props } /> } />
            <Route exact path='/patientsolutions/enroll/ownership/significant-owner' render={() => <SignificantOwner isInReview={isInReview} { ...this.props } /> } />
            <Route exact path='/patientsolutions/enroll/payment-plans' render={() => <PaymentPlans isInReview={isInReview} { ...this.props } /> } />
            <Route exact path='/patientsolutions/enroll/ach-information' render={() => <AchInformation isInReview={isInReview} { ...this.props } /> } />
            <Route exact path='/patientsolutions/enroll/review' render={() => <Review { ...this.props } /> } />
            <Route exact path='/patientsolutions/enroll/agreement' render={() => <Agreement { ...this.props } /> } />
            <Route exact path='/patientsolutions/enroll/thank-you' render={() => <ThankYou { ...this.props } /> } />
          </Switch>
        </main>
        <footer>
            {additionalFooter ? getAdditionalFooterContent() : null }
            <p>Credit terms will depend upon the applicant&apos;s credit score and other key financing characteristics, including but not limited to the amount financed, loan term length, and the applicant&apos;s credit usage and history, as well as the applicant&apos;s selected service provider. Applications are subject to credit approval.</p>
            <p>Installment loans are issued by LendingClub Bank, N.A., Member FDIC, Equal Housing Lender (&ldquo;LendingClub Bank&rdquo;), a wholly-owned subsidiary of LendingClub Corporation, NMLS ID 167439. Revolving lines of credit are issued by Comenity Capital Bank, Member FDIC.</p>
            <p>&ldquo;LendingClub&rdquo; and the &ldquo;LC&rdquo; symbol, and &ldquo;Your Tuition Solution,&rdquo; are trademarks of LendingClub Bank.</p>
            <p>&copy; {`${new Date().getFullYear()}`} LendingClub Bank. All rights reserved.</p>
        </footer>
    </ScrollToTop>;
  }
}

Main.defaultProps = {
  displayLegalUpdates: false
}

export { Main };

const mapStateToProps = (state) => {
  return {
    hasIncompleteOwner: actions.hasIncompleteOwner(state) !== void(0),
    ...state
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators({...actions }, dispatch)
  };
};


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Main));

